import { Static, Type } from '@sinclair/typebox';
import { GoogleFeedRunStatus, GoogleReserveStatus } from './translation_namespace/google_reserve_namespace';

/**
 * « Réserver avec Google » — schémas partagés pro + admin.
 * cf. docs/to_implement/google-feed-merge-v1-v2-plan.md (D5 workflow pro-level,
 * D11 sélection presta 100 % automatique).
 */

/**
 * Conversion Reserve with Google (intégration Redirect) — quand Google amène un
 * utilisateur sur le tunnel (`/book/{slug}?rwg_token=...`), le partenaire doit
 * renvoyer un événement de conversion à Google à la réservation aboutie.
 * `merchant_changed` : 2 = réservation chez le pro du lien Google, 1 = chez un autre pro.
 */
export const GoogleReserveConversionRequestSchema = Type.Object({
  rwg_token: Type.String({ minLength: 1, maxLength: 2048 }),
  merchant_changed: Type.Integer({ minimum: 1, maximum: 2 }),
});
export type GoogleReserveConversionRequestObject = Static<typeof GoogleReserveConversionRequestSchema>;

/** Raisons d'exclusion d'une presta hors lint (les codes lint viennent de GoogleContentLintRule). */
export enum GoogleReservePrestationExclusion {
  NOT_BOOKABLE = 'NOT_BOOKABLE',
  RIDE_TYPE = 'RIDE_TYPE',
  /**
   * Aucune ligne de prix déclarable à Google : en pratique « sur devis » (QUOTE), qui n'est
   * jamais réservable en ligne en v2. Un prix indéterminé (NONE), lui, passe en
   * `INTERPRETATION_NOT_DISPLAYED` — Google n'impose pas de prix ferme.
   */
  PRICE_NOT_DECLARABLE = 'PRICE_NOT_DECLARABLE',
}

/** Raisons de skip d'un lieu (cf. D10 multi-lieux, activation opt-in par lieu). */
export enum GoogleReservePlaceExclusion {
  /** Le pro n'a pas activé ce lieu (opt-in explicite requis, y compris lieu par défaut). */
  NOT_ENABLED = 'NOT_ENABLED',
  ADDRESS_INCOMPLETE = 'ADDRESS_INCOMPLETE',
  /** Lieux activés au-delà du quota de fiches du plan (ex. après downgrade). */
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
}

export const GoogleReserveChecklistSchema = Type.Object({
  has_active_subscription: Type.Boolean(),
  has_establishment_place: Type.Boolean(),
  /** ≥ 1 lieu activé ET émettable (adresse complète + fiche GBP si secondaire + dans le quota). */
  has_enabled_place: Type.Boolean(),
  has_phone: Type.Boolean(),
  has_open_availability: Type.Boolean(),
  has_eligible_prestation: Type.Boolean(),
});
export type GoogleReserveChecklistObject = Static<typeof GoogleReserveChecklistSchema>;

export const GoogleReservePrestationVerdictSchema = Type.Object({
  prestation_id: Type.Number(),
  name: Type.String(),
  is_eligible: Type.Boolean(),
  /** Codes GoogleReservePrestationExclusion + GoogleContentLintRule bloquants. */
  exclusion_reasons: Type.Array(Type.String()),
  /** Codes GoogleContentLintRule non bloquants (ex. PROMOTIONAL_WORDING). */
  warnings: Type.Array(Type.String()),
});
export type GoogleReservePrestationVerdictObject = Static<typeof GoogleReservePrestationVerdictSchema>;

export const GoogleReservePlaceVerdictSchema = Type.Object({
  pro_place_id: Type.Number(),
  label: Type.String(),
  is_default: Type.Boolean(),
  /** Opt-in du pro sur ce lieu (pro_place.google_reserve_enabled). */
  is_enabled: Type.Boolean(),
  is_emitted: Type.Boolean(),
  /** Code GoogleReservePlaceExclusion si is_emitted = false. */
  exclusion_reason: Type.Optional(Type.String()),
});
export type GoogleReservePlaceVerdictObject = Static<typeof GoogleReservePlaceVerdictSchema>;

export const GoogleReserveStatusResponseSchema = Type.Object({
  status: Type.Enum(GoogleReserveStatus),
  requested_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'requested_at' } })),
  reviewed_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'reviewed_at' } })),
  rejection_reason: Type.Optional(Type.String()),
  /** true si le pro peut soumettre la demande (checklist complète + abonnement payant). */
  can_request: Type.Boolean(),
  checklist: GoogleReserveChecklistSchema,
  /** Quota de lieux activables = quota de fiches Google du plan (gmb_connected). */
  place_quota_total: Type.Number(),
  /** Nombre de lieux actuellement activés (google_reserve_enabled). */
  place_quota_used: Type.Number(),
  prestations: Type.Array(GoogleReservePrestationVerdictSchema),
  places: Type.Array(GoogleReservePlaceVerdictSchema),
});
export type GoogleReserveStatusResponseObject = Static<typeof GoogleReserveStatusResponseSchema>;

// =====================================================================
// Admin — /app/admin/google-reserve/*
// =====================================================================

export const AdminGoogleReserveApproveRequestSchema = Type.Object({
  admin_note: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type AdminGoogleReserveApproveRequestObject = Static<typeof AdminGoogleReserveApproveRequestSchema>;

export const AdminGoogleReserveRejectRequestSchema = Type.Object({
  /** Raison communiquée au pro (affichée sur sa page). */
  reason: Type.String({ minLength: 1, maxLength: 2000 }),
  admin_note: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type AdminGoogleReserveRejectRequestObject = Static<typeof AdminGoogleReserveRejectRequestSchema>;

export const AdminGoogleReserveListItemSchema = Type.Object({
  professional_id: Type.Number(),
  slug: Type.String(),
  name: Type.String(),
  status: Type.Enum(GoogleReserveStatus),
  requested_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'requested_at', with_time: true } }),
  ),
  reviewed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'reviewed_at', with_time: true } }),
  ),
  eligible_prestation_count: Type.Number(),
  checklist_complete: Type.Boolean(),
});
export type AdminGoogleReserveListItemObject = Static<typeof AdminGoogleReserveListItemSchema>;

export const AdminGoogleReserveListResponseSchema = Type.Object({
  items: Type.Array(AdminGoogleReserveListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});
export type AdminGoogleReserveListResponseObject = Static<typeof AdminGoogleReserveListResponseSchema>;

export const AdminGoogleReserveProDetailSchema = Type.Object({
  professional_id: Type.Number(),
  slug: Type.String(),
  name: Type.String(),
  phone_number: Type.Optional(Type.String()),
  status: Type.Enum(GoogleReserveStatus),
  requested_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'requested_at', with_time: true } }),
  ),
  reviewed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'reviewed_at', with_time: true } }),
  ),
  admin_note: Type.Optional(Type.String()),
  rejection_reason: Type.Optional(Type.String()),
  checklist: GoogleReserveChecklistSchema,
  prestations: Type.Array(GoogleReservePrestationVerdictSchema),
  places: Type.Array(GoogleReservePlaceVerdictSchema),
});
export type AdminGoogleReserveProDetailObject = Static<typeof AdminGoogleReserveProDetailSchema>;

export const AdminGoogleFeedRunSchema = Type.Object({
  google_feed_run_id: Type.Number(),
  started_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'started_at', with_time: true } }),
  ),
  finished_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'finished_at', with_time: true } }),
  ),
  status: Type.Enum(GoogleFeedRunStatus),
  pros_v2_count: Type.Number(),
  pros_v1_count: Type.Number(),
  pros_skipped_count: Type.Number(),
  services_count: Type.Number(),
  chunks_count: Type.Number(),
  details: Type.Optional(Type.Any()),
  error_message: Type.Optional(Type.String()),
});
export type AdminGoogleFeedRunObject = Static<typeof AdminGoogleFeedRunSchema>;

export const AdminGoogleFeedRunListResponseSchema = Type.Object({
  items: Type.Array(AdminGoogleFeedRunSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});
export type AdminGoogleFeedRunListResponseObject = Static<typeof AdminGoogleFeedRunListResponseSchema>;

/**
 * Export debug des fichiers du feed (dry-run) — un fichier tel qu'il partirait sur
 * le SFTP Google : descriptor (`*.filesetdesc.json`) ou fichier de données. Les
 * données sont servies en JSON lisible (sur le SFTP elles partent gzippées).
 */
export const AdminGoogleFeedExportFileSchema = Type.Object({
  file_name: Type.String(),
  /** Nom du fileset Google (ex. `reservewithgoogle.entity`, `glam.service.v0`). */
  fileset: Type.String(),
  chunk_index: Type.Number(),
  /** Nombre d'éléments dans `content.data` (0 pour un descriptor). */
  items_count: Type.Number(),
  content: Type.Any(),
});
export type AdminGoogleFeedExportFileObject = Static<typeof AdminGoogleFeedExportFileSchema>;

export const AdminGoogleFeedExportResponseSchema = Type.Object({
  /** true si le backend est en mode shadow (GOOGLE_FEED_SHADOW) — indicatif seulement. */
  shadow_mode: Type.Boolean(),
  merchants_count: Type.Number(),
  v2_count: Type.Number(),
  /** Marchands v1 conservés après dédup v2-prioritaire. */
  v1_count: Type.Number(),
  /** Marchands v1 retirés par la dédup (slug déjà présent côté v2). */
  v1_deduped_count: Type.Number(),
  /** 'ok' | 'error' (source v1 injoignable) | 'empty'. */
  v1_status: Type.String(),
  skipped_count: Type.Number(),
  services_count: Type.Number(),
  chunks_count: Type.Number(),
  /** true si le run nocturne refuserait d'uploader (< safety_threshold marchands). */
  below_safety_threshold: Type.Boolean(),
  safety_threshold: Type.Number(),
  files: Type.Array(AdminGoogleFeedExportFileSchema),
});
export type AdminGoogleFeedExportResponseObject = Static<typeof AdminGoogleFeedExportResponseSchema>;
